import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, X, ImageOff, ExternalLink } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { ProductStatusPill } from './ProductStatusPill';
import { ProductThumb } from './ProductThumb';
import { useStickyActionBar } from '@/hooks/useStickyActionBar';
import type { WorkbenchProduct, ReviewDecision } from '@/hooks/useWorkbench';
import { isAutoLoadCtaVisible } from '@/hooks/workbench/emptyStateGates';
import { scoreBadgeVariant } from './scoreBadgeVariant';

interface WorkbenchListViewProps {
  products: WorkbenchProduct[];
  /**
   * Spec B.2: ein Zeilenklick oeffnet das Produkt — auch hier, in der vollen
   * Breite. Genau das war die Luecke ("kein Weg von der Liste ins Produkt"),
   * und sie waere in der breitesten Ansicht geblieben, haette man den Klick
   * nur in der schmalen Leiste gebaut. Der Aufrufer entscheidet, was
   * geschieht: er setzt den Cursor UND klappt die Leiste auf `normal`, damit
   * das Produkt danach auch sichtbar ist.
   */
  onOpenProduct?: (productId: string) => void;
  getReviewDecision: (productId: string) => ReviewDecision;
  wasEdited: (productId: string) => boolean;
  onBulkApprove: (ids: string[]) => Promise<number>;
  onBulkReject: (ids: string[]) => Promise<number>;
  /** P0-4 (Task 6): the list loads via useInfiniteQuery instead of the whole feed. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFetchNextPage?: () => void;
  /** Raw (pre-scoreFilter) count of rows loaded so far — numerator of the honest counter. */
  loadedCount?: number;
  /** Feed-wide pending-review count (search-scoped) — denominator of the honest counter. Null while unknown. */
  pendingTotal?: number | null;
  /** Review-Runde 2, Fund N1: true once automatic loading has hit AUTO_LOAD_ROW_CAP and the server still has more. */
  autoLoadCapReached?: boolean;
  /** Releases one more AUTO_LOAD_ROW_CAP batch of automatic loading (N1 "Weiter suchen" button). */
  onUnlockMoreAutoLoad?: () => void;
  /** F6-Rest: whether a score filter narrows `products` below the raw loaded set — affects whether aria-rowcount can honestly claim the feed-wide total. */
  hasActiveScoreFilter?: boolean;
  /**
   * V3-Befund: in den Reitern "Freigegeben" und "Abgelehnt" ist nichts
   * auswaehlbar (`getReviewDecision` faellt auf den Reiterwert zurueck, also
   * ist `selectableProducts` dort strukturell leer). Die untere Leiste zeigte
   * dort trotzdem "0 Produkte zu pruefen" und zwei dauerhaft gesperrte
   * Sammelknoepfe mit dem Hinweis "Erst Produkte auswaehlen" — eine
   * Handlung, die es in diesen Reitern nicht gibt. Der Aufrufer weiss, in
   * welchem Reiter er steht; false blendet Leiste und Auswahlboxen aus.
   */
  bulkActionsEnabled?: boolean;
  /** K6: Folgeseiten-Abruf rot — Auto-Nachladen stoppt, Fussleiste bietet Retry. */
  loadError?: boolean;
}

// Shared between the sticky header and every virtualized row so columns line
// up. Mirrors the widths the previous `<table>` markup used (w-12/w-16/
// min-w-[200px]/min-w-[340px]/w-20/w-32/w-24) — a real `<table>` can't be
// virtualized (an absolutely positioned `<tr>` blockifies and loses column
// alignment with its siblings), so rows are plain divs on this grid instead.
// F14: die Status-Spalte hatte 6rem, abzueglich `p-4` also 4rem Inhaltsbreite.
// Die Pille "Freigegeben" mit Haken misst rund 7rem und ragte damit sichtbar
// aus ihrer Zelle heraus. 9rem plus `px-3` in der Status-Zelle gibt ihr 7,5rem
// und damit Luft, ohne den beiden Titelspalten (minmax-Untergrenzen 200/340px)
// etwas wegzunehmen.
const LIST_GRID_TEMPLATE = '3rem 4rem minmax(200px,1fr) minmax(340px,1.7fr) 5rem 8rem 9rem';
// T7 (08.09.2026): 56px statt 72 - beide Titelspalten sind seit T7
// einzeilig (kein `whitespace-pre-wrap` mehr), das Bild ist auf 40px
// verkleinert; die Zeile braucht darum keine 72px mehr.
const ROW_ESTIMATE_SIZE = 56;
const ROW_OVERSCAN = 8;
/** Fetch the next page once the last rendered row is within this many rows of the end. */
const PREFETCH_ROWS_FROM_END = 10;
/**
 * Review-Fund F5: die Header-Zeile lebt IM selben Scroll-Element wie die
 * virtualisierten Zeilen (sticky), nimmt dort also echten Platz im normalen
 * Fluss ein. Ohne `scrollMargin` rechnet der Virtualizer `virtualItem.start`
 * relativ zu scrollTop==0 am OBEREN Rand des Scroll-Elements — also dort, wo
 * tatsaechlich der Header sitzt, nicht Zeile 0. Ergebnis waeren Zeilen, die um
 * genau die Header-Hoehe verschoben "sichtbar" sind, und ein abgeschnittener
 * Streifen von exakt dieser Hoehe am Ende (`getTotalSize()` kennt den Header
 * nicht). `scrollMargin` gleicht das aus: `virtualItem.start` bekommt sie
 * automatisch aufaddiert (fuer den Vergleich mit dem echten `scrollTop`,
 * der ja auch den Header mitzaehlt), `getTotalSize()` zieht sie wieder ab
 * (die Sizer-Div braucht nur die reine Zeilenhoehe, der Header beansprucht
 * seinen Platz ja schon selbst). Beim Rendern muss man sie darum wieder
 * abziehen, weil die Sizer-Div ihrerseits erst NACH dem Header im Fluss
 * beginnt (siehe TanStack-Virtual "Window Virtualizing"-Pattern).
 */
const LIST_HEADER_HEIGHT_PX = 48; // h-12

export default function WorkbenchListView({
  products,
  onOpenProduct,
  getReviewDecision,
  wasEdited,
  onBulkApprove,
  onBulkReject,
  hasNextPage = false,
  isFetchingNextPage = false,
  onFetchNextPage,
  loadedCount = 0,
  pendingTotal = null,
  autoLoadCapReached = false,
  onUnlockMoreAutoLoad,
  hasActiveScoreFilter = false,
  bulkActionsEnabled = true,
  loadError = false,
}: WorkbenchListViewProps) {
  const { t } = useTranslation('workbench');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  // 6.4: die Sammel-Ablehnung braucht dieselbe Bestaetigung wie die
  // Sammel-Freigabe (WorkbenchFilterBar) — `rejected` faellt aus allen
  // Werkbank-Abfragen, es gibt keine Ansicht abgelehnter Produkte und keinen
  // Un-Reject; der einzige Rueckweg ist eine gedeckelte Re-Optimierung.
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  // Lifts the floating feedback button above the bulk bar.
  const barRef = useStickyActionBar<HTMLDivElement>();
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: products.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_ESTIMATE_SIZE,
    overscan: ROW_OVERSCAN,
    // F5: Header-Hoehe kompensieren, siehe Kommentar bei der Konstante.
    scrollMargin: LIST_HEADER_HEIGHT_PX,
  });

  // Nachladen, sobald die letzte GERENDERTE Zeile nah am Ende der aktuell
  // geladenen (und ggf. score-gefilterten) Menge liegt. Laeuft auf dem
  // gefilterten `products`-Array, nicht auf der rohen Ladezahl — bewusst
  // einfach gehalten (siehe Task-6-Brief), auch wenn ein aktiver Score-Filter
  // dadurch frueher nachlaedt als noetig.
  const virtualItems = rowVirtualizer.getVirtualItems();
  useEffect(() => {
    // Review-Runde 2, Fund N1: Deckel respektieren. Ohne diese Bedingung
    // wuerde der products.length===0-Zweig unten ein score-gefiltertes,
    // nie treffendes Nachladen endlos weiterfeuern (~690 Requests auf einem
    // 69k-Feed statt der 10 Seiten Deckel).
    // K6-Befund: der baugleiche error-Guard aus Nr. 75 fehlte HIER — die
    // Listenansicht haemmerte eine scheiternde Folgeseite endlos weiter.
    if (loadError || !hasNextPage || isFetchingNextPage || autoLoadCapReached) return;
    // Konsequenz aus F3: der Filter kann die aktuell geladene(n) Seite(n)
    // komplett leerraeumen (0 Treffer), obwohl der Server noch mehr Seiten
    // hat. Ohne diesen Zweig gaebe es KEINE gerenderte Zeile, an deren Index
    // sich "nah am Ende" festmachen liesse (virtualItems waere leer) — die
    // Ansicht bliebe trotz hasNextPage leer haengen, obwohl der aufrufende
    // Leerzustand (WorkbenchReviewer) jetzt genau fuer diesen Fall auf die
    // Liste durchreicht statt selbst "nichts zu tun" anzuzeigen.
    if (products.length === 0) {
      onFetchNextPage?.();
      return;
    }
    const last = virtualItems.at(-1);
    if (!last) return;
    if (last.index >= products.length - PREFETCH_ROWS_FROM_END) {
      onFetchNextPage?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- virtualItems ist ein neues Array pro Render; die tatsaechliche Trigger-Bedingung haengt am letzten Index + den Lade-Flags, nicht an der Array-Identitaet.
  }, [virtualItems, products.length, hasNextPage, isFetchingNextPage, autoLoadCapReached, onFetchNextPage, loadError]);

  // Filter out already reviewed products from selection
  const selectableProducts = useMemo(() => {
    return products.filter(p => getReviewDecision(p.id) === 'pending_review');
  }, [products, getReviewDecision]);

  const selectableIds = useMemo(() => {
    return new Set(selectableProducts.map(p => p.id));
  }, [selectableProducts]);

  const validSelectedIds = useMemo(() => {
    return Array.from(selectedIds).filter(id => selectableIds.has(id));
  }, [selectedIds, selectableIds]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(selectableProducts.map(p => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (productId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(productId);
      } else {
        next.delete(productId);
      }
      return next;
    });
  };

  const handleBulkApprove = async () => {
    if (validSelectedIds.length === 0 || isProcessing) return;

    setIsProcessing(true);
    try {
      const count = await onBulkApprove(validSelectedIds);
      if (count > 0) {
        setSelectedIds(new Set());
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Laeuft erst NACH der Bestaetigung im AlertDialog (siehe unten) — der
  // Knopf in der Fussleiste oeffnet nur den Dialog.
  const handleConfirmBulkReject = async () => {
    if (validSelectedIds.length === 0 || isProcessing) return;

    setIsProcessing(true);
    try {
      const count = await onBulkReject(validSelectedIds);
      if (count > 0) {
        setSelectedIds(new Set());
      }
    } finally {
      setIsProcessing(false);
      setShowBulkRejectDialog(false);
    }
  };

  const truncate = (text: string | null, maxLength: number): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const allSelected = selectableProducts.length > 0 &&
    selectableProducts.every(p => selectedIds.has(p.id));

  // Ohne Konsument, seit die wirkungslose `indeterminate`-Eigenschaft an der
  // Kopfzeilen-Checkbox entfernt ist (siehe Kommentar dort). Bleibt stehen, weil
  // es genau der Wert ist, den der dritte Zustand braucht, sobald er gebaut wird.
  const someSelected = selectableProducts.some(p => selectedIds.has(p.id)) && !allSelected;

  // Ehrlicher Zaehler (P0-4, Task 6): die Liste laedt seitenweise statt den
  // ganzen Feed auf einmal, `total` muss deshalb unabhaengig von der geladenen
  // Menge kommen (pendingTotal). Math.max schuetzt gegen ein kurzes
  // Unterzeichnen, waehrend die 30s-alte Zaehl-Query noch unterwegs ist.
  const honestTotal = Math.max(pendingTotal ?? 0, loadedCount);

  // F6-Rest (a11y): bei aktivem Score-Filter ist honestTotal+1 unehrlich —
  // es waere die feed-weite offene Menge, waehrend `products` (und damit die
  // per aria-rowindex erreichbaren Zeilen) nur der gefilterte Ausschnitt der
  // GELADENEN Seiten ist. -1 ist die ARIA-Konvention fuer "Gesamtzahl
  // unbekannt" — ehrlicher als eine Zahl vorzutaeuschen, die die Zeilen nie
  // erreichen koennen. Ohne Filter bleibt honestTotal+1 (Header-Zeile
  // mitgezaehlt) korrekt, weil `products` dann exakt die geladenen Zeilen sind.
  // K6-Befund: mit roter Zaehl-Query stand hier die bloss geladene Menge als
  // Gesamtzahl im aria-Layer ("Zeile x von 100"). -1 ist die ARIA-Konvention
  // fuer "Gesamtzahl unbekannt" — wie beim Score-Filter.
  const ariaRowCount = hasActiveScoreFilter || pendingTotal === null ? -1 : honestTotal + 1;

  // F11: EINE Bedingung fuer beide Massenaktionen. Getrennt notiert waren sie
  // schon vorher identisch, nur eben zweimal — und genau so driftet ein
  // Zustand auseinander.
  const nothingSelected = validSelectedIds.length === 0;
  const bulkDisabled = isProcessing || nothingSelected;

  return (
    <div className="flex flex-col h-full">
      {/*
        F1 (Blocker): `flex-1` haengt von einer durchgehenden h-full-Kette bis
        zum naechsten begrenzten Vorfahren ab. Die bricht ueber PageTransition
        (klassenloses motion.div) — gemessen: clientHeight==scrollHeight,
        scrollable=false, `main` scrollt stattdessen. Ohne begrenzte Hoehe
        rendert der Virtualizer ALLE Zeilen (outerSize=Gesamthoehe) und der
        Nachlade-Effect feuert bis hasNextPage false ist — bei 69k Produkten
        ~691 Requests + 69k DOM-Zeilen, schlechter als der Ausgangszustand.
        `max-h-[70vh]` gibt dem Scroll-Element eine GARANTIERT begrenzte Hoehe,
        unabhaengig von der (kaputten) Flex-Kette darueber — empirisch
        verifiziert (clientHeight=561, scrollable=true). `flex-1` bleibt
        zusaetzlich stehen (Reviewer-Nit): schadet nicht, solange die
        h-full-Kette kaputt ist, und macht die Komponente zukunftsfest, falls
        sie irgendwann repariert wird (dann darf der Container wieder echten
        verfuegbaren Platz ausfuellen, `max-h-[70vh]` bleibt als Obergrenze).
      */}
      <div
        ref={parentRef}
        className="flex-1 max-h-[70vh] overflow-y-auto"
        role="table"
        aria-rowcount={ariaRowCount}
      >
        <div
          className="grid sticky top-0 z-10 bg-background border-b text-sm font-medium text-muted-foreground"
          style={{ gridTemplateColumns: LIST_GRID_TEMPLATE }}
          role="row"
          aria-rowindex={1}
        >
          <div className="h-12 px-4 flex items-center" role="columnheader">
            {bulkActionsEnabled && (
              <Checkbox
                checked={allSelected}
                /* Hier stand `indeterminate={someSelected}` — WIRKUNGSLOS: die
                   shadcn/Radix-Checkbox kennt diese Eigenschaft nicht, Radix
                   fuehrt den dritten Zustand ueber `checked="indeterminate"`.
                   Der Zustand "teilweise ausgewaehlt" wird also seit dem Einbau
                   nie dargestellt. Offener UI-Fund, bewusst NICHT in diesem
                   Durchgang behoben (Dennis entscheidet ueber den dritten
                   Zustand); `someSelected` oben bleibt als Zutat stehen. */
                onCheckedChange={handleSelectAll}
                aria-label={t('list.selectAll')}
                disabled={selectableProducts.length === 0}
              />
            )}
          </div>
          <div className="h-12 px-4 flex items-center" role="columnheader">{t('list.columns.image')}</div>
          <div className="h-12 px-4 flex items-center" role="columnheader">{t('list.columns.originalTitle')}</div>
          <div className="h-12 px-4 flex items-center" role="columnheader">{t('list.columns.optimizedTitle')}</div>
          <div className="h-12 px-4 flex items-center" role="columnheader">{t('list.columns.score')}</div>
          <div className="h-12 px-4 flex items-center" role="columnheader">{t('list.columns.brand')}</div>
          <div className="h-12 px-3 flex items-center" role="columnheader">{t('list.columns.status')}</div>
        </div>

        {/* Review-Runde 3, Fund N4: kein CTA-Inhalt mehr hier drin — ein
            role="rowgroup" ohne row-Kinder ist ungueltiges ARIA. Bei
            products.length===0 ist diese Sizer-Div einfach leer (0px hoch,
            0 Kinder, das ist ein gueltiges rowgroup); die CTA sitzt jetzt
            in der Fusszeile UNTERHALB von role="table" (siehe dort). */}
        <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }} role="rowgroup">
          {virtualItems.map((virtualRow) => {
            const product = products[virtualRow.index];
            const decision = getReviewDecision(product.id);
            const edited = wasEdited(product.id);
            const isReviewed = decision !== 'pending_review';

            return (
              <div
                key={product.id}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                role="row"
                aria-rowindex={virtualRow.index + 2}
                onClick={onOpenProduct ? () => onOpenProduct(product.id) : undefined}
                className={cn(
                  // T7: feste Zeilenhoehe 56px (h-14). `overflow-hidden` ist die
                  // Ruecksicherung gegen ausreissende Inhalte (z.B. ein sehr
                  // langer Markenname), nicht der Regelfall - die Zellen
                  // darunter sind so bemessen, dass sie ohnehin hineinpassen.
                  'grid h-14 overflow-hidden border-b hover:bg-accent/50 transition-colors',
                  onOpenProduct && 'cursor-pointer',
                  /* V3-Befund: `opacity-60` setzte die Kontrast-Zusicherungen
                     ALLER Bauteile in der Zeile ausser Kraft — Pille, Score,
                     Titel, alles fiel unter AA. Der entschiedene Zustand ist
                     doppelt getragen (getoente Zeilenflaeche unten + Pille);
                     das Abblenden war das dritte, unleserliche Signal. */
                  decision === 'approved' && 'bg-success-light',
                  decision === 'rejected' && 'bg-destructive-light',
                )}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  // F5: scrollMargin wieder abziehen — die Sizer-Div beginnt
                  // erst NACH dem Header im normalen Fluss, waehrend
                  // virtualRow.start relativ zum SCROLLTOP-Nullpunkt (oben am
                  // Header) gerechnet ist. Siehe Kommentar bei
                  // LIST_HEADER_HEIGHT_PX.
                  transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                  gridTemplateColumns: LIST_GRID_TEMPLATE,
                }}
              >
                {/* V1-Befund, Spec B.4 gegen B.2: seit die Zeile anklickbar
                    ist, blubberte der Klick auf die Auswahlbox zum
                    Zeilen-Handler weiter. Ein Haken setzen hiess damit
                    gleichzeitig: Cursor umsetzen und die Liste verlassen
                    (`onOpenProduct` schaltet auf Breite `normal`) — die
                    Sammelauswahl war in genau der Breite unbenutzbar, in der
                    ihre Aktionsleiste sitzt. Die Leiste stoppt hier aus
                    demselben Grund (WorkbenchRail.tsx), der externe
                    Produktlink weiter rechts ebenfalls. */}
                <div
                  className="px-4 py-2 flex items-center"
                  role="cell"
                  onClick={(e) => e.stopPropagation()}
                >
                  {bulkActionsEnabled && (
                    <Checkbox
                      checked={selectedIds.has(product.id)}
                      onCheckedChange={(checked) =>
                        handleSelectOne(product.id, checked as boolean)
                      }
                      disabled={isReviewed}
                      /* Nie product.title: die Spalte traegt bei rund 4.600
                         Produkten unseren eigenen frueheren Output (Spec
                         Datenintegritaet Original-Titel, 5.4). Ein
                         Screenreader nennt hier deshalb den Haendler-Titel
                         und, wo es keinen belegten gibt, den optimierten. */
                      aria-label={t('list.selectProduct', {
                        title: product.original_title || product.optimized_title || '',
                      })}
                    />
                  )}
                </div>
                <div className="px-4 py-2 flex items-center" role="cell">
                  {/* T7: 40px statt 48px - bei 56px Zeilenhoehe reicht das
                      Bild sonst zusammen mit seiner Zellenpolsterung nicht
                      mehr in die Zeile. */}
                  {/* Dieselbe Vorschau wie in der Produktleiste, aus
                      derselben Datei: Bildteller, Platzhalter bei fehlendem
                      Bild UND Platzhalter bei einem Ladefehler. Das dritte
                      fehlte hier -- die Zelle versteckte ein nicht ladbares
                      Bild per `style.display = 'none'`, und die Zeile sah
                      danach aus, als haette das Produkt nie ein Bild gehabt
                      (beutel-shop.ch sitzt hinter Cloudflare, das Hotlinks
                      mit 403 beantwortet). PR #338 hatte den Platzhalter in
                      Kachel und Leiste ergaenzt, diese Datei aber nicht. */}
                  <ProductThumb
                    imageLink={product.image_link ?? null}
                    groesse="h-10 w-10"
                    symbolGroesse="h-5 w-5"
                    testIdPraefix="list"
                  />
                </div>
                {/* T7: Proportionalschrift statt Monospace - ein Merchant-
                    Titel und ein KI-Titel nebeneinander in Monospace lasen
                    sich wie ein Code-Diff statt wie zwei Produktnamen.
                    `min-w-0` + `truncate` statt der reinen JS-Kuerzung: die
                    schnitt zwar den Text bei 60 Zeichen ab, ein schmaler
                    Spaltenbereich liess den Rest trotzdem umbrechen. */}
                {/* Spalte "Original-Titel": das ist original_title und NIE
                    product.title. Letzteres trug bei rund 4.600 Produkten
                    unseren eigenen frueheren Output (09.09.2026), und unter
                    dieser Ueberschrift waere das eine Falschaussage. Ohne
                    belegtes Original steht hier ein neutraler Platzhalter --
                    kein Vorher statt ein falsches (Dennis, 09.09.2026). */}
                <div className="px-4 py-2 text-xs flex min-w-0 items-center" role="cell">
                  <div className="flex min-w-0 items-center gap-1.5">
                    {product.original_title ? (
                      <span className="truncate">{truncate(product.original_title, 60)}</span>
                    ) : (
                      <span className="truncate text-muted-foreground italic">{t('list.noOriginal')}</span>
                    )}
                    {product.link && (
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
                        title={product.link}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
                {/* T7: Proportionalschrift + einzeilig. `whitespace-pre-wrap
                    break-words` liess den Titel umbrechen und war der
                    Hauptgrund fuer die vorherige 112px-Zeilenhoehe - der
                    Volltext gehoert ohnehin in die Karte, hier reicht ein
                    Ueberblick mit Ellipse. */}
                <div className="px-4 py-2 text-xs flex min-w-0 items-center" role="cell">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{product.optimized_title}</span>
                    {edited && (
                      /* K4-Befund: variant="warning" ist weiss auf deckendem
                         Orange (2.13:1 hell / 1.69:1 dunkel) — dieselben
                         Zahlen, die Nr. 46 fuer die Statuspille als MUSS
                         vermessen hat. Tonales Paar wie an der Kachel
                         (`card/ShoppingTile.tsx`, Marke "Deine Fassung"); das
                         gleichnamige Abzeichen im Kartenkopf gibt es seit
                         Etappe 5 nicht mehr. */
                      <Badge className="text-xs bg-warning-light hover:bg-warning-light text-warning-ink">
                        {t('status.edited')}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="px-4 py-2 flex items-center" role="cell">
                  <Badge variant={scoreBadgeVariant(product.totalScore)} className="tabular-nums">
                    {product.totalScore !== null ? Math.round(product.totalScore) : t('list.scoreNotAvailable')}
                  </Badge>
                </div>
                <div className="px-4 py-2 text-sm flex items-center" role="cell">
                  {truncate(product.brand, 20)}
                </div>
                <div className="px-3 py-2 flex items-center" role="cell">
                  {/*
                    Freigabe UND Auslieferung, nicht nur die Freigabe: die
                    Spalte zeigte bisher drei Zustaende aus der
                    Sitzungsentscheidung und verschwieg dabei, ob die Fassung
                    schon bei Google liegt. `decision` bleibt die
                    Freigabe-Quelle (sie ist aktueller als die geladene Zeile,
                    sobald der Nutzer in dieser Sitzung entschieden hat), der
                    Upload-Zustand kommt aus der Zeile.
                  */}
                  <ProductStatusPill
                    approvalStatus={decision}
                    uploadStatus={product.upload_status ?? null}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ehrlicher Zaehler (P0-4, Task 6): die Liste laedt seitenweise, dieser
          Hinweis sagt, wie viel von der offenen Menge tatsaechlich schon da
          ist, statt stillschweigend so zu tun als sei alles geladen.

          Review-Runde 3, Fund N3/N4: die "Weiter laden"-CTA sitzt HIER, nicht
          mehr innerhalb von role="table" (N4 — ein role="rowgroup" ohne
          row-Kinder war ungueltiges ARIA). Sichtbarkeit ist bewusst NICHT an
          `products.length` gekoppelt (isAutoLoadCtaVisible nimmt die Laenge
          gar nicht als Parameter) — der N3-Bug war genau diese Kopplung: die
          CTA blieb unsichtbar, sobald der Deckel waehrend produktiven
          Blaetterns (Treffer vorhanden) erreicht wurde, nicht nur beim
          leergefilterten Sonderfall. Die Copy unterscheidet trotzdem, ob
          Treffer da sind ("keine Treffer" vs. "X geladen, weitere laden?"). */}
      <div className="flex-shrink-0 border-t bg-muted/30 px-4 py-2 pb-16 text-center text-xs text-muted-foreground space-y-2">
        <p>
          {hasNextPage
            ? pendingTotal === null
              // K5-Befund: mit roter Zaehl-Query stand hier "100 von 100
              // geladen" NEBEN einem aktiven Weiter-laden-Zustand. Ist die
              // Gesamtzahl nicht ermittelbar, wird sie weggelassen.
              ? t('listLoadedUnknownTotal', { loaded: loadedCount })
              : t('listLoaded', { loaded: loadedCount, total: honestTotal })
            : t('listLoadedAll', { total: honestTotal })}
        </p>
        {loadError && hasNextPage && (
          <div className="flex items-center justify-center gap-2 pt-1">
            <span className="text-destructive-ink">{t('listLoadFailed')}</span>
            <Button variant="outline" size="sm" onClick={() => onFetchNextPage?.()}>
              {t('listLoadFailedRetry')}
            </Button>
          </div>
        )}
        {isAutoLoadCtaVisible(autoLoadCapReached, hasNextPage) && (
          <div className="flex flex-col items-center gap-2 pt-1">
            <p className="text-sm text-foreground">
              {products.length === 0
                ? t('listAutoLoadCapped', { count: loadedCount })
                : t('listAutoLoadCappedMore', { count: loadedCount })}
            </p>
            <Button variant="outline" size="sm" onClick={() => onUnlockMoreAutoLoad?.()}>
              {products.length === 0
                ? t('listAutoLoadCappedAction')
                : t('listAutoLoadCappedMoreAction')}
            </Button>
          </div>
        )}
      </div>

      {/* Fixed Bottom Bar — centered actions, matches card-view WorkbenchActionBar so the
          buttons sit in the middle (clear of the bottom-right floating feedback button).
          V3-Befund: in den Reitern Freigegeben/Abgelehnt ist nichts auswaehlbar —
          dort stand "0 Produkte zu pruefen" neben zwei dauerhaft gesperrten
          Knoepfen, deren Hinweis eine unmoegliche Handlung verlangte.

          T7: dieselbe Regel wie in `WorkbenchRail` seit Etappe 3 —
          `validSelectedIds.length > 0` in der Bedingung. Ein dauerhafter
          Balken mit gesperrten Knoepfen kostet Hoehe und behauptet eine
          Handlung, die es gerade nicht gibt. Die Rail-Leiste zeigt OHNE
          Auswahl ueberhaupt keinen Zaehlertext (nur mit Auswahl), darum
          verschwindet hier bewusst die GANZE Leiste samt Zaehler — es gibt
          keinen Text darin, der nur ohne Auswahl gebraucht wuerde: die
          Gesamtzahl der offenen Produkte steht bereits im ehrlichen Zaehler
          der Fussleiste eine Ebene hoeher. */}
      {bulkActionsEnabled && validSelectedIds.length > 0 && (
      <div
        ref={barRef}
        className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-3 pb-3 border-t border-border bg-background/95 backdrop-blur-sm shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
      >
        <span className="absolute left-4 sm:left-6 lg:left-8 text-sm font-medium text-muted-foreground hidden md:block">
          {t('list.selectedOfTotal', { selected: validSelectedIds.length, total: selectableProducts.length })}
        </span>
        {/* F11: beide Knoepfe teilen dieselbe Sperrbedingung, sahen im
            gesperrten Zustand aber verschieden aus — die Outline-Variante
            behielt ihren vollen Rahmenkontrast und las sich als aktiv, die
            gefuellte verblasste. Der Zustand war damit nicht ablesbar
            (2.6: Zustaende sind Craft). Beide bekommen jetzt dieselbe
            gedaempfte Sperrflaeche, und der Grund steht als Hinweis am
            Gruppen-Container: `disabled:pointer-events-none` aus den
            Button-Varianten verschluckt jeden `title` am Knopf selbst. */}
        <div className="flex gap-3" title={nothingSelected ? t('list.bulkNeedsSelection') : undefined}>
          <Button
            variant="outline"
            onClick={() => setShowBulkRejectDialog(true)}
            disabled={bulkDisabled}
            className="gap-2 disabled:border-border disabled:bg-muted disabled:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            {t('list.rejectSelected', { count: validSelectedIds.length })}
          </Button>
          {/* K8-Befund: variant=default ist Weiss auf Indigo (4.41/2.95 —
              die Nr.-50-Messwerte); der Fix #164 stellte nur den Rail-Knopf
              um. Gleiche success-Flaeche wie dort. */}
          <Button
            onClick={handleBulkApprove}
            disabled={bulkDisabled}
            className="gap-2 bg-success hover:bg-success-hover text-success-foreground disabled:bg-muted disabled:text-muted-foreground"
          >
            <Check className="h-4 w-4" />
            {t('list.approveSelected', { count: validSelectedIds.length })}
          </Button>
        </div>
      </div>
      )}

      {/* 6.4: Bestaetigung vor der Sammel-Ablehnung — gleiches Muster wie der
          Sammelfreigabe-Dialog in WorkbenchFilterBar. Der linke der beiden
          gleich grossen Knoepfe konnte bis zu 1000 markierte Produkte in
          EINEM Klick unumkehrbar ablehnen, waehrend die Freigabe direkt
          daneben vorher fragte. */}
      <AlertDialog open={showBulkRejectDialog} onOpenChange={setShowBulkRejectDialog}>
        <AlertDialogContent
          onCloseAutoFocus={() => {
                      /* V3-Befund: Radix gibt den Fokus an das zuvor fokussierte
                         Element zurueck — das kann durch die BESTAETIGTE Handlung
                         verschwunden sein (letztes Produkt des Reiters, geleerte
                         Auswahl). Dann faellt der Fokus auf <body> und Tab beginnt
                         wieder ganz oben. Nach dem Standardverhalten pruefen, und
                         falls nichts angekommen ist, auf den aktiven Reiter setzen:
                         der existiert immer. */
                      queueMicrotask(() => {
              /* Microtask statt setTimeout: ein Timer kann den Unmount (und
                 in Tests den Abbau der DOM-Umgebung) ueberleben — genau das
                 hat den CI-Lauf mit "document is not defined" gerissen,
                 waehrend die Suite lokal nur durch Timing gruen war. Die
                 Microtask laeuft noch im selben Task, direkt nach Radix'
                 eigener Fokus-Rueckgabe, und kann nichts ueberleben. */
              if (typeof document === 'undefined') return;
              if (document.activeElement === document.body) {
                document
                  .querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
                  ?.focus();
              }
            });
                    }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('bulkRejectConfirm.title', { count: validSelectedIds.length })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkRejectConfirm.body', { count: validSelectedIds.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>
              {t('bulkRejectConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmBulkReject} disabled={isProcessing}>
              {isProcessing ? t('bulkRejectConfirm.confirmLoading') : t('bulkRejectConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
